import { DatePicker } from '@mui/lab';
import { Grid } from '@mui/material';
import { Fragment, memo, useEffect, useMemo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { FormikErrors, FormikTouched } from 'formik';
import { SectionVM } from 'civil-status/view-models/step-form';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import { FormControlLabel } from '@mui/material';
import { Checkbox } from '@mui/material';
import { FormControl } from '@mui/material';
import { RadioGroup } from '@mui/material';
import { Radio } from '@mui/material';
import { CodeExtremesDecree } from 'civil-status/constants/code-extremes-decree';

interface ActConsentProps {
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  configuration?: string;
  keySection?: string;
  setDataInterprete: (isWithInterprete?: boolean) => void;
}

const CitizenshipBaseComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  configuration,
  setDataInterprete,
}: ActConsentProps): React.ReactElement => {
  const isBaseCit07: boolean = useMemo(() => {
    return configuration === ConfigurationType.BASECIT07;
  }, [configuration]);

  const isBaseCit06: boolean = useMemo(() => {
    return configuration === ConfigurationType.BASECIT06;
  }, [configuration]);

  useEffect(() => {
    setDataInterprete(values?.isWithInterprete);
  }, [values?.isWithInterprete]);

  return (
    <Grid container spacing={2} item>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data decreto conferimento *"
          aria-label="Data decreto conferimento"
          value={values?.dateConferralDecree || null}
          onChange={(date) => {
            onChange('dateConferralDecree', date);
          }}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="dateConferralDecree"
              fullWidth
              helperText={errors?.dateConferralDecree}
              error={touched?.dateConferralDecree && Boolean(errors?.dateConferralDecree)}
            />
          )}
        />
      </Grid>
      {isBaseCit06 || isBaseCit07 ? (
        <Grid item xs={12} md={4}>
          <DatePicker
            label={`Data registrazione alla Corte dei Conti ${isBaseCit07 ? '*' : ''}`}
            aria-label="Data registrazione alla corte dei conti"
            value={values?.dateRegistrationCourt || null}
            onChange={(date) => {
              onChange('dateRegistrationCourt', date);
            }}
            disabled={disabled}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="dateRegistrationCourt"
                fullWidth
                helperText={errors?.dateRegistrationCourt}
                error={touched?.dateRegistrationCourt && Boolean(errors?.dateRegistrationCourt)}
              />
            )}
          />
        </Grid>
      ) : undefined}
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data notifica del decreto *"
          aria-label="Data notifica del decreto"
          value={values?.dateNotificationDecree || null}
          onChange={(date) => {
            onChange('dateNotificationDecree', date);
          }}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="dateNotificationDecree"
              fullWidth
              helperText={errors?.dateNotificationDecree}
              error={touched?.dateNotificationDecree && Boolean(errors?.dateNotificationDecree)}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Estremi decreto *"
          aria-label="Estremi decreto"
          id="extremesDecree"
          name="extremesDecree"
          fullWidth
          disabled={disabled}
          helperText={errors?.extremesDecree}
          error={touched?.extremesDecree && Boolean(errors?.extremesDecree)}
          type="text"
          variant="outlined"
          value={values?.extremesDecree || ''}
          onChange={(event) => {
            onChange('extremesDecree', event.target.value);
          }}
        />
      </Grid>
      {isBaseCit06 ? (
        <Grid container spacing={2} item xs={12}>
          <Grid item xs={12} md={8}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                aria-label="Tipo Estremi Decreto"
                id="typeExtremesDecree"
                defaultValue={true}
                value={values?.typeExtremesDecree || ''}
                onChange={(event) => onChange('typeExtremesDecree', event.target.value || '')}
              >
                <FormControlLabel
                  value={CodeExtremesDecree.Normale}
                  control={<Radio />}
                  label="Normale"
                  disabled={disabled}
                />
                <FormControlLabel
                  value={CodeExtremesDecree.Riammissione}
                  control={<Radio />}
                  label="Riammissione"
                  disabled={disabled}
                />
                <FormControlLabel
                  value={CodeExtremesDecree.Ritardo}
                  control={<Radio />}
                  label="Ritardo"
                  disabled={disabled}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          {values?.typeExtremesDecree === CodeExtremesDecree.Riammissione ? (
            <Grid item xs={12} md={4}>
              <PmiTextField
                label="Estremi Pec"
                aria-label="Estremi Pec"
                id="extremesPec"
                name="extremesPec"
                fullWidth
                disabled={disabled}
                helperText={errors?.extremesPec}
                error={touched?.extremesPec && Boolean(errors?.extremesPec)}
                type="text"
                variant="outlined"
                value={values?.extremesPec || ''}
                onChange={(event) => {
                  onChange('extremesPec', event.target.value);
                }}
              />
            </Grid>
          ) : undefined}
        </Grid>
      ) : undefined}
      {isBaseCit07 ? (
        <Fragment>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  id="isWithInterprete"
                  checked={values?.isWithInterprete || false}
                  onChange={() => {
                    onChange('isWithInterprete', !values?.isWithInterprete);
                  }}
                  inputProps={{ 'aria-label': 'Presenza interprete' }}
                  disabled={disabled}
                />
              }
              label="Presenza interprete"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  id="transcript"
                  checked={values?.isInterestedPresent || false}
                  onChange={() => {
                    onChange('isInterestedPresent', !values?.isInterestedPresent);
                  }}
                  inputProps={{ 'aria-label': 'Interessato presente' }}
                  disabled={disabled}
                />
              }
              label="Interessato presente"
            />
          </Grid>
        </Fragment>
      ) : undefined}
    </Grid>
  );
};

CitizenshipBaseComponent.displayName = 'Form Citizenship Base component';

const CitizenshipBase = memo(CitizenshipBaseComponent);

export default CitizenshipBase;
